import { createMediaStoreURL } from '@/api'
import { components as batteryComponents } from '@/battery-types'
import { steeringAuthInit } from '@/stores/auth'
import { MediaStoreFile } from '@/types'
import { aggregateWithIdsParameter, useRetrieveHelpers } from '@/util'
import {
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  BaseRequestOptions,
  createExtendableAPI,
  ExtendableAPI,
} from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'
import { computed, MaybeRefOrGetter, toValue } from 'vue'

export function useFileId(
  fileId: MaybeRefOrGetter<MediaStoreFile['id'] | string | object | null | undefined>,
) {
  return computed(() => {
    let _id = toValue(fileId)
    if (_id === null || _id === undefined) return null
    if (typeof _id === 'number') return _id
    if (typeof _id === 'object') {
      if (!('source' in _id && 'type' in _id)) return null
      if (_id.type !== 'aura-structuredaudio' || typeof _id.source !== 'string') return null
      _id = _id.source
    }
    _id = _id.split('/').at(-1)
    if (!_id) return null
    _id = parseInt(_id)
    return Number.isNaN(_id) ? null : _id
  })
}

export const useFilesStore = defineStore('files', () => {
  const { api, base } = createExtendableAPI<MediaStoreFile>(
    createMediaStoreURL.prefix('files'),
    steeringAuthInit,
  )
  const listOperations = APIListPaginated(api)
  const retrieveOperations = APIRetrieve(api, {
    aggregate: aggregateWithIdsParameter(listOperations.listIsolated),
  })

  return {
    ...base,
    ...listOperations,
    ...retrieveOperations,
    ...APIRemove(api),
    ...APIUpdate<MediaStoreFile, batteryComponents['schemas']['FileUpdateRequest']>(api),
    ...APILog(api),
    ...useRetrieveHelpers({ ...base, ...retrieveOperations }),
  }
})

function APILog(api: ExtendableAPI<MediaStoreFile>) {
  async function retrieveLog(
    id: MediaStoreFile['id'],
    options: BaseRequestOptions | undefined = undefined,
  ) {
    const query = options?.query ?? new URLSearchParams()
    const res = await fetch(
      api.createRequest(api.endpoint(id, 'logs', query), options?.requestInit),
    )
    await api.maybeThrowResponse(res)
    return await res.text()
  }

  return { retrieveLog }
}
